import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer } from 'react-leaflet';
import type { ReactNode } from 'react';
import type { MapConfig } from './geoApi.js';

export interface ResolvedTiles {
  url: string;
  attribution: string;
  subdomains: string[] | string;
}

/**
 * Resolve Leaflet tile settings from the backend-resolved map provider (A-7).
 * Google tiles are used when the provider resolved to GOOGLE (a key was
 * configured); Mapbox when a token exists; otherwise free OpenStreetMap.
 */
export function resolveTiles(cfg: MapConfig | null): ResolvedTiles {
  const provider = cfg?.provider ?? 'OSM';
  if (provider === 'GOOGLE') {
    return {
      url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
      attribution: 'Map data &copy; Google',
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    };
  }
  if (provider === 'MAPBOX' && cfg?.mapboxAccessToken) {
    return {
      url: `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${cfg.mapboxAccessToken}`,
      attribution: 'Map data &copy; Mapbox',
      subdomains: 'abc',
    };
  }
  return {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    subdomains: 'abc',
  };
}

interface GeoMapProps {
  center: [number, number];
  zoom: number;
  tiles: ResolvedTiles;
  height?: number;
  ariaLabel: string;
  children?: ReactNode;
}

/**
 * Reusable Leaflet map (§9 GIS map engine). Uses CircleMarker children (no image
 * assets required). Provider is chosen by the caller via {@link resolveTiles}.
 */
export function GeoMap({ center, zoom, tiles, height = 480, ariaLabel, children }: GeoMapProps): ReactNode {
  return (
    <div aria-label={ariaLabel} style={{ height, width: '100%' }}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer url={tiles.url} attribution={tiles.attribution} subdomains={tiles.subdomains} />
        {children}
      </MapContainer>
    </div>
  );
}
